import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Printer, 
  Wifi, 
  Zap, 
  FileText, 
  CheckCircle2,
  Monitor,
  Scan
} from 'lucide-react';

export const EquipmentCatalog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const equipment = [
    {
      id: 1,
      brand: 'Canon',
      model: 'Canon IR ADV DX C3730',
      category: 'color',
      type: 'Multifuncional Color',
      speed: '30 ppm',
      volume: '5,000 - 15,000',
      image: '/C3730.jpg',
      colorScheme: { bg: 'bg-blue-100', text: 'text-blue-800' },
      features: [
        'Impresión y copiado a color y bn',
        'Escaneo a color y bn',
        'Dúplex automático de un solo paso',
        'Soporta carta, oficio y doble carta',
        'Resolución 1200 x 1200 ppp',
        'Pantalla táctil a color'
      ],
      connectivity: ['Ethernet', 'WiFi', 'USB'],
      ideal: 'Oficinas de volumen medio a alto',
    },
    {
      id: 2,
      brand: 'Canon',
      model: 'Canon IR ADV C357',
      category: 'color',
      type: 'Multifuncional Color',
      speed: '35 ppm',
      volume: '5,000 - 10,000',
      image: '/C357.png',
      colorScheme: { bg: 'bg-green-100', text: 'text-green-800' },
      features: [
        'Impresión y copiado a color y bn',
        'Escaneo bilateral en un solo paso',
        'Dúplex automático',
        'Soporta carta y oficio',
        'Resolución 1200 x 1200 ppp',
        'ADF con escaneo dúplex'
      ],
      connectivity: ['Ethernet', 'WiFi', 'USB'],
      ideal: 'Oficinas de volumen medio',
    },
    {
      id: 3,
      brand: 'Canon',
      model: 'Canon IR ADV DX 4735',
      category: 'bn',
      type: 'Multifuncional Blanco y Negro',
      speed: '35 ppm',
      volume: '5,000 - 15,000',
      image: '/4735.png',
      colorScheme: { bg: 'bg-slate-100', text: 'text-slate-800' },
      features: [
        'Impresión y copiado bn',
        'Escaneo a color y bn',
        'Dúplex automático de un solo paso',
        'Soporta carta, oficio y doble carta',
        'Resolución 1200 x 1200 ppp',
        'Alta capacidad de papel'
      ],
      connectivity: ['Ethernet', 'WiFi', 'USB'],
      ideal: 'Oficinas de volumen medio a alto',
    },
    {
      id: 4,
      brand: 'HP',
      model: 'HP LaserJet Managed E62655',
      category: 'bn',
      type: 'Multifuncional Blanco y Negro',
      speed: '55 ppm',
      volume: '8,000 - 30,000',
      image: '/HP-M62655-BN.jpg',
      colorScheme: { bg: 'bg-purple-100', text: 'text-purple-800' },
      features: [
        'Impresión y copiado bn de alto rendimiento',
        'Escaneo avanzado',
        'Dúplex automático',
        'Soporta carta y oficio',
        'Seguridad HP Secure Boot',
        'Gestión centralizada HP Web Jetadmin'
      ],
      connectivity: ['Ethernet', 'WiFi', 'USB'],
      ideal: 'Alto volumen empresarial (hasta 100,000 páginas)',
    },
  ];

  const categories = [
    { id: 'all', name: 'Todos', icon: <Printer className="h-4 w-4" />, color: 'bg-gray-600' },
    { id: 'color', name: 'Color', icon: <Monitor className="h-4 w-4" />, color: 'bg-blue-600' },
    { id: 'bn', name: 'Blanco y Negro', icon: <FileText className="h-4 w-4" />, color: 'bg-gray-800' },
  ];

  const filteredEquipment = selectedCategory === 'all' 
    ? equipment 
    : equipment.filter(eq => eq.category === selectedCategory);

  return (
    <section id="equipos" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-primary font-semibold text-sm uppercase tracking-wide">
            Nuestro Catálogo
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-3">
            Equipos Canon y HP Disponibles
          </h2>
          <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
            Equipos multifuncionales de última generación para todo tipo de necesidades empresariales
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex justify-center gap-4 mb-12 flex-wrap"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedCategory === cat.id
                  ? `${cat.color} text-white shadow-lg`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.icon}
              <span>{cat.name}</span>
            </button>
          ))}
        </motion.div>

        {/* Equipment Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {filteredEquipment.map((equip, index) => (
            <motion.div
              key={equip.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              {/* Image Section - Increased height */}
              <div className="relative bg-gray-50 h-80 flex items-center justify-center p-8 overflow-hidden">
                <img 
                  src={equip.image} 
                  alt={`${equip.brand} ${equip.model}`}
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-300 filter drop-shadow-lg"
                />
                <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                  {equip.speed}
                </div>
                <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-md">
                  <span className="text-lg font-bold text-gray-800">{equip.brand}</span>
                </div>
              </div>

              {/* Header Card - Simplified */}
              <div className="p-6">
                <div className="mb-4">
                  <div className={`inline-block px-4 py-2 rounded-lg mb-2 ${equip.colorScheme.bg}`}>
                    <h3 className={`text-xl font-bold mb-0 ${equip.colorScheme.text}`}>{equip.model}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{equip.type}</p>
                </div>

                {/* Volume */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-green-50 p-2 rounded-lg flex-shrink-0">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">Volumen Mensual</h4>
                    <p className="text-gray-600 text-sm">{equip.volume} páginas/mes</p>
                  </div>
                </div>

                {/* Connectivity */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-purple-50 p-2 rounded-lg flex-shrink-0">
                    <Wifi className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">Conectividad</h4>
                    <div className="flex gap-2 flex-wrap">
                      {equip.connectivity.map((conn, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium"
                        >
                          {conn}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">Características Principales</h4>
                  <div className="space-y-2">
                    {equip.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ideal for */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-gray-900 mb-1 text-sm">Ideal para:</h4>
                  <p className="text-gray-600 text-sm">{equip.ideal}</p>
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    const element = document.getElementById('contacto');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Solicitar Información
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center bg-gradient-to-br from-gray-50 to-red-50 rounded-3xl p-8 sm:p-12"
        >
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            ¿No encuentras el equipo ideal?
          </h3>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Contáctanos y te ayudaremos a encontrar el equipo perfecto para tus necesidades específicas
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const element = document.getElementById('contacto');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors duration-200 shadow-lg inline-flex items-center space-x-2"
          >
            <span>Consultar con Experto</span>
            <Zap className="h-5 w-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default EquipmentCatalog;
